import { useRef, type ReactNode } from 'react';

/**
 * A board that can be dragged to any size and zoomed.
 *
 * Shared by the lesson plan and the calendar, which want the same thing for
 * the same reason: they are one big grid on an otherwise empty page, and how
 * much room they need depends on the week, the screen and the eyesight of
 * whoever is reading them.
 */

/* --------------------------------------------------------------- sizing */

/** Half size is unreadable; double is bigger than any screen needs. */
export const MIN_ZOOM = 0.6;
export const MAX_ZOOM = 2;

export const MIN_BOARD_WIDTH = 600;
const MIN_BOARD_HEIGHT = 240;

export interface BoardSize {
  /** Null means the full width available. */
  width: number | null;
  /** Null means as tall as the table. */
  height: number | null;
}

export function readNumber(key: string, fallback: number, min: number, max: number): number {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? Math.min(Math.max(value, min), max) : fallback;
  } catch {
    return fallback;
  }
}

export function readBoard(key: string): BoardSize {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? 'null') as BoardSize | null;
    return {
      width: typeof raw?.width === 'number' ? Math.max(raw.width, MIN_BOARD_WIDTH) : null,
      height: typeof raw?.height === 'number' ? Math.max(raw.height, MIN_BOARD_HEIGHT) : null,
    };
  } catch {
    return { width: null, height: null };
  }
}

/**
 * Any page-sized thing, at whatever size and zoom the teacher set.
 *
 * Zoom is the CSS `zoom` property rather than a transform: it re-lays the
 * contents out at the new size, so a table's columns stay aligned and the
 * thing still scrolls sensibly. A transform would scale the pixels and leave
 * the scrollbars measuring the old box.
 */
export function ResizableBoard({
  zoom,
  size,
  onResize,
  loading,
  children,
}: {
  zoom: number;
  size: BoardSize;
  onResize: (size: BoardSize) => void;
  loading: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  function begin(e: React.PointerEvent) {
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);

    // Measured, so the first pixel of drag continues from where it is.
    gesture.current = {
      x: e.clientX,
      y: e.clientY,
      w: ref.current?.offsetWidth ?? MIN_BOARD_WIDTH,
      h: ref.current?.offsetHeight ?? MIN_BOARD_HEIGHT,
    };
  }

  function move(e: React.PointerEvent) {
    const g = gesture.current;
    if (!g) return;

    onResize({
      width: Math.max(g.w + (e.clientX - g.x), MIN_BOARD_WIDTH),
      height: Math.max(g.h + (e.clientY - g.y), MIN_BOARD_HEIGHT),
    });
  }

  function end(e: React.PointerEvent) {
    (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
    gesture.current = null;
  }

  return (
    <div
      ref={ref}
      className={['relative print:!h-auto print:!w-auto', loading ? 'opacity-50' : ''].join(' ')}
      style={{ width: size.width ?? undefined, height: size.height ?? undefined }}
    >
      <div
        className="h-full overflow-auto print:overflow-visible"
        // `zoom` scales the layout, not just the pixels, so the columns stay
        // where they belong and the scrollbars measure the real thing.
        style={{ zoom }}
      >
        {children}
      </div>

      <span
        role="slider"
        tabIndex={0}
        aria-label="Resize the plan"
        aria-valuenow={Math.round(size.width ?? 0)}
        aria-valuemin={MIN_BOARD_WIDTH}
        aria-valuemax={4000}
        onPointerDown={begin}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 100 : 20;
          const w = size.width ?? ref.current?.offsetWidth ?? MIN_BOARD_WIDTH;
          const h = size.height ?? ref.current?.offsetHeight ?? MIN_BOARD_HEIGHT;

          if (e.key === 'ArrowRight') onResize({ ...size, width: w + step });
          if (e.key === 'ArrowLeft')
            onResize({ ...size, width: Math.max(w - step, MIN_BOARD_WIDTH) });
          if (e.key === 'ArrowDown') onResize({ ...size, height: h + step });
          if (e.key === 'ArrowUp')
            onResize({ ...size, height: Math.max(h - step, MIN_BOARD_HEIGHT) });
        }}
        title="Drag to any size you like"
        className="absolute -bottom-1 -right-1 flex h-7 w-7 cursor-nwse-resize items-center justify-center rounded text-clay hover:bg-linen hover:text-terracotta print:hidden"
        style={{ touchAction: 'none' }}
      >
        ◢
      </span>
    </div>
  );
}

/**
 * The −, percentage and + controls, plus Fit.
 *
 * Kept with the board rather than written twice, because two copies of a
 * control drift: one gains a step the other does not.
 */
export function BoardControls({
  zoom,
  onZoom,
  onFit,
}: {
  zoom: number;
  onZoom: (zoom: number) => void;
  onFit: () => void;
}) {
  const step = (by: number) =>
    onZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((zoom + by) * 10) / 10)));

  return (
    <div className="flex items-center gap-1">
      <span className="mr-1 text-xs text-stone">Size</span>

      <button
        onClick={() => step(-0.1)}
        disabled={zoom <= MIN_ZOOM}
        className="btn-primary px-2.5 py-1"
        aria-label="Smaller"
        title="Smaller"
      >
        −
      </button>

      <button
        onClick={() => onZoom(1)}
        className="btn-primary min-w-[62px] px-2 py-1 text-xs"
        title="Back to the normal size"
      >
        {Math.round(zoom * 100)}%
      </button>

      <button
        onClick={() => step(0.1)}
        disabled={zoom >= MAX_ZOOM}
        className="btn-primary px-2.5 py-1"
        aria-label="Bigger"
        title="Bigger"
      >
        +
      </button>

      <button
        onClick={onFit}
        className="btn-primary ml-1"
        title="Back to the full width and its natural height"
      >
        Fit
      </button>
    </div>
  );
}
